/**
 * OnboardingSettingsPage — Manage onboarding templates and their items.
 *
 * HR admins create/edit templates per department/role with configurable task items.
 * Each item has: title, type, category, assignee, due offset, required flag.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Stack, Chip, IconButton,
  Dialog, DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, Alert, LinearProgress, Tooltip,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ExpandMore, Star as DefaultIcon,
} from '@mui/icons-material';
import {
  onboardingApi,
  OnboardingTemplate,
  OnboardingTemplateItem,
} from '@/api/endpoints/hr';
import { TYPE } from '@/theme/typography';
import { useNotificationStore } from '@/stores/notificationStore';
import PageHelpIcon from '@/components/PageHelpIcon';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { ConfirmDialog } from '@/components/ConfirmDialog';
const CATEGORIES = [
  { value: 'pre_start', label: 'Pre-Start' },
  { value: 'day_one', label: 'Day 1' },
  { value: 'week_one', label: 'Week 1' },
  { value: 'month_one', label: 'Month 1' },
];

const TASK_TYPES = [
  { value: 'checklist', label: 'Checklist' },
  { value: 'document', label: 'Document Upload' },
  { value: 'form', label: 'Form' },
  { value: 'content', label: 'Content / Training' },
  { value: 'approval', label: 'Approval' },
];

const ASSIGNEE_TYPES = [
  { value: 'employee', label: 'Employee' },
  { value: 'hr', label: 'People' },
  { value: 'manager', label: 'Manager' },
  { value: 'buddy', label: 'Buddy' },
  { value: 'it', label: 'IT' },
  { value: 'finance', label: 'Finance' },
];

const CATEGORY_LABELS: Record<string, string> = {
  pre_start: 'Pre-Start', day_one: 'Day 1', week_one: 'Week 1', month_one: 'Month 1',
};

// ============================================================
// TEMPLATE DIALOG
// ============================================================
function TemplateDialog({
  open, onClose, template,
}: {
  open: boolean;
  onClose: () => void;
  template?: OnboardingTemplate | null;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    is_default: template?.is_default || false,
    auto_transition: template?.auto_transition || '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      template
        ? onboardingApi.updateTemplate(template.id, data)
        : onboardingApi.createTemplate(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] });
      showSuccess(template ? 'Template updated' : 'Template created');
      onClose();
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed'),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BrandedDialogTitle>{template ? 'Edit Template' : 'New Onboarding Template'}</BrandedDialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField label="Name" value={form.name} required
          onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
        <TextField label="Description" value={form.description} multiline rows={2}
          onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
        <TextField select label="Auto-Transition on Completion" value={form.auto_transition}
          onChange={(e) => setForm(f => ({ ...f, auto_transition: e.target.value }))}>
          <MenuItem value="">Manual (no auto-transition)</MenuItem>
          <MenuItem value="probation">Probation</MenuItem>
          <MenuItem value="active">Active</MenuItem>
        </TextField>
        <FormControlLabel
          control={<Switch checked={form.is_default} onChange={(e) => setForm(f => ({ ...f, is_default: e.target.checked }))} />}
          label="Default template (used when no dept/role match)" />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.name || mutation.isPending}
          onClick={() => mutation.mutate({
            name: form.name,
            description: form.description || undefined,
            is_default: form.is_default,
            auto_transition: form.auto_transition || undefined,
          })}>
          {mutation.isPending ? 'Saving...' : template ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// ITEM DIALOG
// ============================================================
function ItemDialog({
  open, onClose, templateId, item,
}: {
  open: boolean;
  onClose: () => void;
  templateId: string;
  item?: OnboardingTemplateItem | null;
}) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [form, setForm] = useState({
    title: item?.title || '',
    description: item?.description || '',
    task_type: item?.task_type || 'checklist',
    category: item?.category || 'pre_start',
    assignee_type: item?.assignee_type || 'hr',
    is_required: item?.is_required ?? true,
    due_day_offset: item?.due_day_offset ?? 0,
    document_name: item?.document_name || '',
    content_url: item?.content_url || '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      item
        ? onboardingApi.updateItem(templateId, item.id, data)
        : onboardingApi.addItem(templateId, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] });
      showSuccess(item ? 'Item updated' : 'Item added');
      onClose();
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed'),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BrandedDialogTitle>{item ? 'Edit Task' : 'Add Task to Template'}</BrandedDialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField label="Title" value={form.title} required
          onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
        <TextField label="Description" value={form.description} multiline rows={2}
          onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
        <Stack direction="row" spacing={2}>
          <TextField select label="Type" value={form.task_type} sx={{ flex: 1 }}
            onChange={(e) => setForm(f => ({ ...f, task_type: e.target.value as typeof f.task_type }))}>
            {TASK_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField select label="Category" value={form.category} sx={{ flex: 1 }}
            onChange={(e) => setForm(f => ({ ...f, category: e.target.value as typeof f.category }))}>
            {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={2}>
          <TextField select label="Assignee" value={form.assignee_type} sx={{ flex: 1 }}
            onChange={(e) => setForm(f => ({ ...f, assignee_type: e.target.value as typeof f.assignee_type }))}>
            {ASSIGNEE_TYPES.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
          <TextField label="Due (days from start)" type="number" value={form.due_day_offset} sx={{ flex: 1 }}
            onChange={(e) => setForm(f => ({ ...f, due_day_offset: Number(e.target.value) }))} />
        </Stack>
        {form.task_type === 'document' && (
          <TextField label="Document Name" value={form.document_name}
            helperText="e.g. 'NIN Slip', 'Signed Offer Letter'"
            onChange={(e) => setForm(f => ({ ...f, document_name: e.target.value }))} />
        )}
        {form.task_type === 'content' && (
          <TextField label="Content URL" value={form.content_url}
            helperText="Link to handbook, video, or training material"
            onChange={(e) => setForm(f => ({ ...f, content_url: e.target.value }))} />
        )}
        <FormControlLabel
          control={<Switch checked={form.is_required} onChange={(e) => setForm(f => ({ ...f, is_required: e.target.checked }))} />}
          label="Required (blocks completion if not done)" />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.title || mutation.isPending}
          onClick={() => mutation.mutate({
            title: form.title,
            description: form.description || undefined,
            task_type: form.task_type,
            category: form.category,
            assignee_type: form.assignee_type,
            is_required: form.is_required,
            due_day_offset: form.due_day_offset,
            document_name: form.document_name || undefined,
            content_url: form.content_url || undefined,
          })}>
          {mutation.isPending ? 'Saving...' : item ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================
export default function OnboardingSettingsPage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [templateDialog, setTemplateDialog] = useState<{ open: boolean; template?: OnboardingTemplate | null }>({ open: false });
  const [itemDialog, setItemDialog] = useState<{ open: boolean; templateId: string; item?: OnboardingTemplateItem | null }>({ open: false, templateId: '' });
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-templates'],
    queryFn: () => onboardingApi.listTemplates(),
    select: (res) => res.data,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => onboardingApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] });
      showSuccess('Template deleted');
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed to delete'),
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ templateId, itemId }: { templateId: string; itemId: string }) =>
      onboardingApi.deleteItem(templateId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-templates'] });
      showSuccess('Task removed');
    },
    onError: (err: any) => showError(err.response?.data?.detail || 'Failed'),
  });

  const templates = data?.items || [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <PageHelpIcon pageKey="onboarding-settings" />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setTemplateDialog({ open: true })}>
          New Template
        </Button>
      </Stack>

      {isLoading && <LinearProgress />}

      {!isLoading && templates.length === 0 && (
        <Alert severity="info">
          No onboarding templates yet. Create one to define the tasks new employees should complete.
        </Alert>
      )}

      {templates.map((tmpl) => (
        <Accordion key={tmpl.id} defaultExpanded={tmpl.is_default} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, mr: 2 }}>
              <Typography fontWeight={TYPE.semibold}>{tmpl.name}</Typography>
              {tmpl.is_default && <Chip icon={<DefaultIcon />} label="Default" size="small" color="primary" />}
              {!tmpl.is_active && <Chip label="Inactive" size="small" color="default" />}
              {tmpl.auto_transition && (
                <Chip label={`Auto → ${tmpl.auto_transition}`} size="small" color="info" variant="outlined" />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {tmpl.items.length} tasks
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {tmpl.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {tmpl.description}
              </Typography>
            )}

            <Stack direction="row" gap={1} sx={{ mb: 2 }}>
              <Button size="small" startIcon={<EditIcon />}
                onClick={() => setTemplateDialog({ open: true, template: tmpl })}>
                Edit Template
              </Button>
              <Button size="small" startIcon={<AddIcon />}
                onClick={() => setItemDialog({ open: true, templateId: tmpl.id })}>
                Add Task
              </Button>
              <Button size="small" color="error" startIcon={<DeleteIcon />}
                onClick={() => setDeleteTemplateId(tmpl.id)}>
                Delete
              </Button>
            </Stack>

            {/* Group items by category */}
            {['pre_start', 'day_one', 'week_one', 'month_one'].map(cat => {
              const items = tmpl.items.filter(i => i.category === cat);
              if (items.length === 0) return null;
              return (
                <Box key={cat} sx={{ mb: 2 }}>
                  <Typography variant="overline" fontWeight={TYPE.bold} color="text.secondary">
                    {CATEGORY_LABELS[cat]} ({items.length})
                  </Typography>
                  <List dense disablePadding>
                    {items.sort((a, b) => a.sort_order - b.sort_order).map(item => (
                      <ListItem key={item.id} sx={{ pl: 2 }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Typography variant="body2">{item.title}</Typography>
                              <Chip label={item.task_type} size="small" variant="outlined" sx={{ fontSize: TYPE.xs, height: 18 }} />
                              <Chip label={item.assignee_type} size="small" variant="outlined" sx={{ fontSize: TYPE.xs, height: 18 }} />
                              {item.is_required && <Chip label="Required" size="small" color="error" variant="outlined" sx={{ fontSize: TYPE.xs, height: 18 }} />}
                            </Stack>
                          }
                          secondary={`Day ${item.due_day_offset >= 0 ? '+' : ''}${item.due_day_offset}${item.document_name ? ` · Doc: ${item.document_name}` : ''}`}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Edit">
                            <IconButton size="small"
                              onClick={() => setItemDialog({ open: true, templateId: tmpl.id, item })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error"
                              onClick={() => deleteItemMutation.mutate({ templateId: tmpl.id, itemId: item.id })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}

            {tmpl.items.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No tasks yet. Add tasks to define what new employees should complete.
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Dialogs */}
      {templateDialog.open && (
        <TemplateDialog open={templateDialog.open}
          template={templateDialog.template}
          onClose={() => setTemplateDialog({ open: false })} />
      )}
      {itemDialog.open && (
        <ItemDialog open={itemDialog.open}
          templateId={itemDialog.templateId}
          item={itemDialog.item}
          onClose={() => setItemDialog({ open: false, templateId: '' })} />
      )}
      <ConfirmDialog
        open={!!deleteTemplateId}
        title="Delete this template?"
        description="All checklist items in this template will also be removed."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => { if (deleteTemplateId) deleteMutation.mutate(deleteTemplateId); }}
        onClose={() => setDeleteTemplateId(null)}
      />
    </Box>
  );
}
